import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAddressCard,
  faCircleHalfStroke,
  faCircleInfo,
  faCircleUser,
  faComments,
  faCrown,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CloseButton as ClosableLink } from '@headlessui/react';
import classNames from 'classnames';
import { appRoutes } from 'config/routing';
import { Link, usePathname } from 'core/i18n/navigation';
import React from 'react';
import { useTranslations } from 'next-intl';

interface RowProps {
  content: string;
  icon: IconDefinition;
  href?: string;
  isActive?: boolean;
  onClick?: () => void;
}

const Row: React.FC<RowProps> = ({
  content,
  icon,
  href,
  isActive,
  onClick,
}) => {
  const inner = (
    <>
      <FontAwesomeIcon
        icon={icon}
        className={styles.row.icon(isActive)}
        aria-hidden="true"
      />
      <span className={styles.row.label(isActive)}>{content}</span>
    </>
  );

  if (href) {
    return (
      <ClosableLink
        as={Link}
        href={href}
        className={styles.row.container(isActive)}
      >
        {inner}
      </ClosableLink>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.row.container(isActive)}
    >
      {inner}
    </button>
  );
};

const Group: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className={styles.group}>
    <p className={styles.groupLabel}>{label}</p>
    {children}
  </div>
);

const SettingsMenu = () => {
  const t = useTranslations();
  const pathname = usePathname();
  const isProfileActive = pathname === appRoutes.SETTINGS.PROFILE;
  const isAccountActive = pathname === appRoutes.SETTINGS.ACCOUNT;
  const isSubscriptionActive =
    pathname.startsWith(appRoutes.SETTINGS.SUBSCRIPTION) ||
    pathname.startsWith(appRoutes.SETTINGS.CHECKOUT.DEFAULT);
  const isAppearanceActive = pathname === appRoutes.SETTINGS.APPEARANCE;
  const isFeedbackActive = pathname === appRoutes.SETTINGS.FEEDBACK;
  const isHelpActive = pathname === appRoutes.SETTINGS.HELP;

  return (
    <div className={styles.container}>
      <Group label={t('settingsNav.groups.general')}>
        <Row
          content={t('profileSettings.title')}
          icon={faCircleUser}
          href={appRoutes.SETTINGS.PROFILE}
          isActive={isProfileActive}
        />
        <Row
          content={t('accountSettings.title')}
          icon={faAddressCard}
          href={appRoutes.SETTINGS.ACCOUNT}
          isActive={isAccountActive}
        />
      </Group>

      <Group label={t('accountMenu.pro')}>
        <Row
          content={t('billing.page.title')}
          icon={faCrown}
          href={appRoutes.SETTINGS.SUBSCRIPTION}
          isActive={isSubscriptionActive}
        />
      </Group>

      <Group label={t('settingsNav.groups.preferences')}>
        <Row
          content={t('appearanceSettings.title')}
          icon={faCircleHalfStroke}
          href={appRoutes.SETTINGS.APPEARANCE}
          isActive={isAppearanceActive}
        />
      </Group>

      <Group label={t('settingsNav.groups.helpUsImprove')}>
        <Row
          content={t('feedbackSettings.title')}
          icon={faComments}
          href={appRoutes.SETTINGS.FEEDBACK}
          isActive={isFeedbackActive}
        />
      </Group>

      <Group label={t('settingsNav.groups.about')}>
        <Row
          content={t('helpSettings.title')}
          icon={faCircleInfo}
          href={appRoutes.SETTINGS.HELP}
          isActive={isHelpActive}
        />
      </Group>
    </div>
  );
};

export default SettingsMenu;

const styles = {
  container: 'flex flex-col',
  // A little air between rows, so a chosen row and a hovered neighbour
  // don't run into one another.
  group: 'flex flex-col gap-1',
  groupLabel:
    'px-3 pt-5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-secondary',
  row: {
    // The same row as a chosen collection in the box sidebar: a quiet
    // pill with a fine edge, the icon sitting bare like an emoji.
    container: (isActive?: boolean) =>
      classNames(
        'group flex w-full flex-row items-center gap-3 rounded-[10px] border px-3 py-2 text-left',
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
        isActive
          ? 'border-surface-selection-border bg-surface-selection'
          : 'border-transparent hover:bg-ink-secondary/8',
      ),
    icon: (isActive?: boolean) =>
      classNames(
        'fa-fw w-5 text-[17px]',
        isActive ? 'text-accent-ink' : 'text-ink-primary',
      ),
    label: (isActive?: boolean) =>
      classNames(
        'text-[15px] text-ink-primary',
        isActive ? 'font-semibold' : 'font-medium',
      ),
  },
};
